import React, { useEffect, useState } from 'react';
import { ISettingOption } from '@biolibtech/biolib-js';
import { useGetDataRecordVersionQuery } from '@apiHooks';
import { utf8ToBase64 } from '../../../../../apiHooks/utils';
import MultiSelect from './MultiSelect';

interface IOptionsFile {
    options: Record<string, string>;
}

interface IProps {
    optionsFilePath: string;
    resourceVersionUuid: string;
    selectedOptions: ISettingOption[];
    setSelectedOptions: (options: ISettingOption[]) => void;
    singleSelect?: boolean;
    defaultValue?: string;
}

export default function MultiSelectWithOptionsFile(props: IProps) {
    const { optionsFilePath, resourceVersionUuid, selectedOptions, setSelectedOptions, singleSelect, defaultValue } = props;
    const [allOptions, setAllOptions] = useState<ISettingOption[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { data: versionData } = useGetDataRecordVersionQuery({ resourceVersionUuid });

    useEffect(() => {
        if (!versionData?.presigned_download_url) {
            return;
        }

        const abortController = new AbortController();
        const timeoutId = setTimeout(() => {
            abortController.abort();
            setError('Failed to load options file: request timed out');
        }, 60_000);
        const filePath = optionsFilePath.replace('assets://', '');
        const fileUrl = `${location.origin}/proxy/files/v1/data/?upstream_url_base64=${utf8ToBase64(
            versionData.presigned_download_url,
        )}&path_base64=${utf8ToBase64(`/${filePath}`)}`;

        setError(null);
        setAllOptions(null);

        fetch(fileUrl, { signal: abortController.signal })
            .then((response) => {
                if (response.status !== 200) {
                    throw new Error(`Failed to fetch options file. Got HTTP status: ${response.status}`);
                }
                return response.json();
            })
            .then((data: unknown) => {
                clearTimeout(timeoutId);
                const parsed = data as IOptionsFile;
                if (!parsed.options || typeof parsed.options !== 'object' || Array.isArray(parsed.options)) {
                    throw new Error('Invalid options file: expected an object with an "options" field');
                }
                const options: ISettingOption[] = Object.entries(parsed.options).map(([key, value]) => ({
                    key,
                    value,
                }));
                setAllOptions(options);
                if (defaultValue && selectedOptions.length === 0) {
                    if (singleSelect) {
                        const match = options.find((opt) => opt.value === defaultValue);
                        if (match) {
                            setSelectedOptions([match]);
                        }
                    } else {
                        const defaultMatches = defaultValue
                            .split(',')
                            .map((val) => options.find((opt) => opt.value === val))
                            .filter((opt): opt is ISettingOption => opt !== undefined);
                        if (defaultMatches.length > 0) {
                            setSelectedOptions(defaultMatches);
                        }
                    }
                }
            })
            .catch((error: Error) => {
                clearTimeout(timeoutId);
                if (!abortController.signal.aborted) {
                    setError(`Failed to load options file: ${error.message}`);
                }
            });

        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [versionData?.presigned_download_url, optionsFilePath]);

    if (error) {
        return <p className='text-red-600 text-sm'>{error}</p>;
    }

    return (
        <MultiSelect
            allOptions={allOptions ?? []}
            isLoading={allOptions === null}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            singleSelect={singleSelect}
        />
    );
}
